import { type PropsWithChildren } from 'react';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { buildScale } from '../../core/utils/core-scale.js';
import { HistogramXScale } from '../contexts/HistogramXScale.context.js';
import { useCurrentDomainState } from '../hooks/store/selectors.js';
import { useHistogramFilteredData } from '../hooks/useHistogramFilteredData.js';
import { useHistogramZoomRanges } from '../hooks/useHistogramZoomRanges.js';
import { useXAxisConfig } from '../hooks/useXAxisConfig.js';

export const HistogramXScaleProvider = ({ children }: PropsWithChildren) => {
  const { min: minConfig, max: maxConfig, scale } = useXAxisConfig();
  const { minMaxXValue } = useHistogramFilteredData();
  const { initialZoom } = useHistogramZoomRanges();
  const [initialZoomMin, initialZoomMax] = initialZoom ?? [];
  const { width } = useGridPlotSize();
  const configMinMax = {
    min: initialZoomMin ?? minConfig,
    max: initialZoomMax ?? maxConfig,
  };

  const initialXScale = buildScale(
    scale,
    [0, width],
    minMaxXValue,
    configMinMax,
  );
  const [min, max] = initialXScale.domain();
  const minMaxDomain = { min, max };

  const domain = useCurrentDomainState();
  const xScale = domain ? initialXScale.copy().domain(domain) : initialXScale;

  return (
    <HistogramXScale.Provider
      value={{
        initialXScale: initialXScale,
        xScale,
        minMaxBoundaries: minMaxDomain,
      }}
    >
      {children}
    </HistogramXScale.Provider>
  );
};

HistogramXScaleProvider['displayName'] = 'HistogramXScaleProvider';
